import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import {CommandIconButton, GlobalCommands} from '@github-ui/ui-commands'
import {ArrowLeftIcon, CopyIcon, ScreenFullIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import type {DialogHeaderProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useMemo} from 'react'

import {BUTTON_LABELS} from '../constants/buttons'
import {LABELS} from '../constants/labels'
import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from '../contexts/IssueCreateDataContext'
import {DisplayMode} from '../utils/display-mode'
import {IssueCreationKind} from '../utils/model'
import {appendChooseToBasePath} from '../utils/urls'
import styles from './CreateIssueDialogHeader.module.css'

type CreateIssueDialogHeaderProps = {
  navigate: (url: string) => void
} & DialogHeaderProps

export const CreateIssueDialogHeader = ({navigate, dialogLabelId, onClose}: CreateIssueDialogHeaderProps) => {
  const {displayMode, setDisplayMode, optionConfig, isSubIssue} = useIssueCreateConfigContext()
  const {repository, repositoryAbsolutePath, template, resetMetadata, setTemplate} = useIssueCreateDataContext()
  const defaultIssueCreateRepoEnabled = isFeatureEnabled('memex_default_issue_create_repository')

  const onCloseClick = useCallback(() => {
    onClose('close-button')
  }, [onClose])

  const chooseUrlForCopy = useMemo(() => appendChooseToBasePath(repositoryAbsolutePath), [repositoryAbsolutePath])

  // For template selection, we want to show a "Copy URL" button, while for issue creation we can show a fullscreen navigation.
  const showCopyUrlButton = displayMode === DisplayMode.TemplatePicker && repository !== undefined
  const showFullScreenButton =
    displayMode === DisplayMode.IssueCreation &&
    optionConfig.showFullScreenButton &&
    navigate !== noop &&
    repository !== undefined

  const handleBackToTemplates = useCallback(() => {
    // Clear metadata with preservation logic for sub-issues
    resetMetadata({
      repository,
      selectedRepository: repository, // Staying in same repository when going back to templates
    })

    setTemplate(undefined)
    setDisplayMode(DisplayMode.TemplatePicker)
  }, [resetMetadata, setTemplate, setDisplayMode, repository])

  const navigateToFullscreen = () => {
    if (!showFullScreenButton) {
      return
    }

    const baseUrl = `/${repository.nameWithOwner}/issues/new`
    const templateFileName = !template || template.kind === IssueCreationKind.BlankIssue ? undefined : template.fileName
    const link = templateFileName ? `${baseUrl}?template=${encodeURIComponent(templateFileName)}` : baseUrl
    navigate(link)
  }

  const repositoryNameWithOwner = repository?.nameWithOwner

  const dialogTitle = useMemo(() => {
    // If we are adding this as a child of another issue, then we want to update the label to match
    const relationType = isSubIssue ? 'sub-issue' : 'issue'

    if (displayMode === DisplayMode.TemplatePicker || !repositoryNameWithOwner) {
      const initialTitle = optionConfig.issueCreateArguments?.initialValues?.title
      return LABELS.issueCreateDialogTitleTemplatePane(relationType) + (initialTitle ? ` - "${initialTitle}"` : '')
    }

    // If it's a blank issue then we don't want to show the template suffix
    const templateName = !template || template.kind === IssueCreationKind.BlankIssue ? undefined : template.name

    if (displayMode === DisplayMode.IssueDuplication) {
      return LABELS.issueCreateDialogTitleDuplicationPane(repositoryNameWithOwner, relationType)
    }

    return LABELS.issueCreateDialogTitleCreationPane(repositoryNameWithOwner, templateName, relationType)
  }, [displayMode, isSubIssue, repositoryNameWithOwner, template, optionConfig])

  return (
    <Dialog.Header>
      <GlobalCommands commands={{'issue-create:open-fullscreen': navigateToFullscreen}} />
      <div className={styles.CreateIssueDialogHeaderContainer}>
        {defaultIssueCreateRepoEnabled && displayMode === DisplayMode.IssueCreation && (
          <IconButton
            icon={ArrowLeftIcon}
            aria-label="Back to templates"
            variant="invisible"
            onClick={handleBackToTemplates}
            className={styles.CopyToClipboardButton}
          />
        )}
        <div className={styles.dialogTitleSection}>
          <Dialog.Title id={dialogLabelId}>{dialogTitle}</Dialog.Title>
        </div>
        {showCopyUrlButton && (
          <CopyToClipboardButton
            textToCopy={chooseUrlForCopy}
            ariaLabel={BUTTON_LABELS.copyUrl}
            icon={CopyIcon}
            tooltipProps={{direction: 'n'}}
            className={styles.CopyToClipboardButton}
          />
        )}
        {showFullScreenButton && (
          <CommandIconButton
            variant="invisible"
            commandId="issue-create:open-fullscreen"
            icon={ScreenFullIcon}
            className={styles.CopyToClipboardButton}
          />
        )}
        <Dialog.CloseButton onClose={onCloseClick} />
      </div>
    </Dialog.Header>
  )
}
